import type {Direction, Node} from '@react-types/shared';

import type {GridCollection} from '@react-types/grid';
import type {Key, RefObject} from 'react';
import {
  getChildNodes,
  getFirstItem,
  getLastItem,
  getNthItem,
} from '../../@react-stately/collections';
import type {Layout} from '../../@react-stately/virtualizer';
import {Rect} from '../../@react-stately/virtualizer';
import type {KeyboardDelegate} from './types';

export interface GridKeyboardDelegateOptions<T, C> {
  collection: C;
  disabledKeys: Set<Key>;
  ref?: RefObject<HTMLElement>;
  direction: Direction;
  collator?: Intl.Collator;
  layout?: Layout<Node<T>>;
  focusMode?: 'row' | 'cell';
}

export class GridKeyboardDelegate<T, C extends GridCollection<T>>
  implements KeyboardDelegate
{
  collection: C;

  protected disabledKeys: Set<Key>;

  protected ref?: RefObject<HTMLElement>;

  protected direction: Direction;

  protected collator?: Intl.Collator;

  protected layout?: Layout<Node<T>>;

  protected focusMode;

  constructor(options: GridKeyboardDelegateOptions<T, C>) {
    this.collection = options.collection;
    this.disabledKeys = options.disabledKeys;
    this.ref = options.ref;
    this.direction = options.direction;
    this.collator = options.collator;
    this.layout = options.layout;
    this.focusMode = options.focusMode || 'row';
  }

  protected isCell(node: Node<T>) {
    return node.type === 'cell';
  }

  protected isRow(node: Node<T>) {
    return node.type === 'row' || node.type === 'item';
  }

  protected findPreviousKey(fromKey?: Key, pred?: (item: Node<T>) => boolean) {
    let key =
      fromKey != null
        ? this.collection.getKeyBefore(fromKey)
        : this.collection.getLastKey();

    while (key != null) {
      const item = this.collection.getItem(key);
      if (!this.disabledKeys.has(key) && (!pred || pred(item!))) {
        return key;
      }

      key = this.collection.getKeyBefore(key);
    }
  }

  protected findNextKey(fromKey?: Key, pred?: (item: Node<T>) => boolean) {
    let key =
      fromKey != null
        ? this.collection.getKeyAfter(fromKey)
        : this.collection.getFirstKey();

    while (key != null) {
      const item = this.collection.getItem(key);
      if (!this.disabledKeys.has(key) && (!pred || pred(item!))) {
        return key;
      }

      key = this.collection.getKeyAfter(key);
    }
  }

  getKeyBelow(key: Key) {
    const startItem = this.collection.getItem(key);
    if (!startItem) {
      return;
    }

    let keyBelow: Key | undefined = key;

    // If focus was on a cell, start searching from the parent row
    if (this.isCell(startItem)) {
      keyBelow = startItem.parentKey!;
    }

    // Find the next item
    keyBelow = this.findNextKey(keyBelow);
    if (keyBelow != null) {
      // If focus was on a cell, focus the cell with the same index in the next row.
      if (this.isCell(startItem)) {
        const item = this.collection.getItem(keyBelow)!;
        return getNthItem(
          getChildNodes(item, this.collection),
          startItem.index!,
        )?.key;
      }

      // Otherwise, focus the next row
      if (this.focusMode === 'row') {
        return keyBelow;
      }
    }
  }

  getKeyAbove(key: Key) {
    const startItem = this.collection.getItem(key);
    if (!startItem) {
      return;
    }

    let keyAbove: Key | undefined = key;

    // If focus is on a cell, start searching from the parent row
    if (this.isCell(startItem)) {
      keyAbove = startItem.parentKey!;
    }

    // Find the previous item
    keyAbove = this.findPreviousKey(keyAbove);
    if (keyAbove != null) {
      // If focus was on a cell, focus the cell with the same index in the previous row.
      if (this.isCell(startItem)) {
        const item = this.collection.getItem(keyAbove)!;
        return getNthItem(
          getChildNodes(item, this.collection),
          startItem.index!,
        )?.key;
      }

      // Otherwise, focus the previous row
      if (this.focusMode === 'row') {
        return keyAbove;
      }
    }
  }

  getKeyRightOf(key: Key) {
    const item = this.collection.getItem(key);
    if (!item) {
      return;
    }

    // If focus is on a row, focus the first child cell.
    if (this.isRow(item)) {
      const children = getChildNodes(item, this.collection);
      return this.direction === 'rtl'
        ? getLastItem(children)?.key
        : getFirstItem(children)?.key;
    }

    // If focus is on a cell, focus the next cell if any,
    // otherwise focus the parent row.
    if (this.isCell(item)) {
      const parent = this.collection.getItem(item.parentKey!)!;
      const children = getChildNodes(parent, this.collection);
      const next =
        this.direction === 'rtl'
          ? getNthItem(children, item.index! - 1)
          : getNthItem(children, item.index! + 1);

      if (next) {
        return next.key;
      }

      // focus row only if focusMode is set to row
      if (this.focusMode === 'row') {
        return item.parentKey;
      }

      return this.direction === 'rtl'
        ? this.getFirstKey(key)
        : this.getLastKey(key);
    }
  }

  getKeyLeftOf(key: Key) {
    const item = this.collection.getItem(key);
    if (!item) {
      return;
    }

    // If focus is on a row, focus the last child cell.
    if (this.isRow(item)) {
      const children = getChildNodes(item, this.collection);
      return this.direction === 'rtl'
        ? getFirstItem(children)?.key
        : getLastItem(children)?.key;
    }

    // If focus is on a cell, focus the previous cell if any,
    // otherwise focus the parent row.
    if (this.isCell(item)) {
      const parent = this.collection.getItem(item.parentKey!)!;
      const children = getChildNodes(parent, this.collection);
      const prev =
        this.direction === 'rtl'
          ? getNthItem(children, item.index! + 1)
          : getNthItem(children, item.index! - 1);

      if (prev) {
        return prev.key;
      }

      // focus row only if focusMode is set to row
      if (this.focusMode === 'row') {
        return item.parentKey;
      }

      return this.direction === 'rtl'
        ? this.getLastKey(key)
        : this.getFirstKey(key);
    }
  }

  getFirstKey(key?: Key, global?: boolean) {
    let item: Node<T> | null = null;
    if (key != null) {
      item = this.collection.getItem(key);
      if (!item) {
        return;
      }

      // If global flag is not set, and a cell is currently focused,
      // move focus to the first cell in the parent row.
      if (this.isCell(item) && !global) {
        const parent = this.collection.getItem(item.parentKey!)!;
        return getFirstItem(getChildNodes(parent, this.collection))?.key;
      }
    }

    // Find the first row
    let nextKey = this.findNextKey();

    // If global flag is set (or if focus mode is cell), focus the first cell in the first row.
    if (
      (nextKey != null && item && this.isCell(item) && global) ||
      this.focusMode === 'cell'
    ) {
      const item = this.collection.getItem(nextKey!)!;
      nextKey = getFirstItem(getChildNodes(item, this.collection))?.key;
    }

    // Otherwise, focus the row itself.
    return nextKey;
  }

  getLastKey(key?: Key, global?: boolean) {
    let item: Node<T> | null = null;
    if (key != null) {
      item = this.collection.getItem(key);
      if (!item) {
        return;
      }

      // If global flag is not set, and a cell is currently focused,
      // move focus to the last cell in the parent row.
      if (this.isCell(item) && !global) {
        const parent = this.collection.getItem(item.parentKey!);
        const children = getChildNodes(parent!, this.collection);
        return getLastItem(children)?.key;
      }
    }

    // Find the last row
    let previousKey = this.findPreviousKey();

    // If global flag is set (or if focus mode is cell), focus the last cell in the last row.
    if (
      (previousKey != null && item && this.isCell(item) && global) ||
      this.focusMode === 'cell'
    ) {
      const item = this.collection.getItem(previousKey!)!;
      const children = getChildNodes(item, this.collection);
      previousKey = getLastItem(children)?.key;
    }

    // Otherwise, focus the row itself.
    return previousKey;
  }

  private getItem(key: Key): HTMLElement | undefined | null {
    return this.ref?.current?.querySelector(`[data-key="${key}"]`);
  }

  private getItemRect(key: Key): Rect | undefined {
    if (this.layout) {
      return this.layout.getLayoutInfo(key)?.rect;
    }

    const item = this.getItem(key);
    if (item) {
      return new Rect(
        item.offsetLeft,
        item.offsetTop,
        item.offsetWidth,
        item.offsetHeight,
      );
    }
  }

  private getPageHeight(): number | undefined {
    if (this.layout) {
      return this.layout.virtualizer?.visibleRect.height;
    }

    return this.ref?.current?.offsetHeight;
  }

  private getContentHeight(): number | undefined {
    if (this.layout) {
      return this.layout.getContentSize().height;
    }

    return this.ref?.current?.scrollHeight;
  }

  getKeyPageAbove(key: Key) {
    let itemRect = this.getItemRect(key);
    if (!itemRect) {
      return null;
    }

    let keyAbove: Key | undefined = key;
    const pageY = Math.max(0, itemRect.maxY - this.getPageHeight()!);

    while (itemRect && itemRect.y > pageY) {
      keyAbove = this.getKeyAbove(keyAbove!);
      itemRect = this.getItemRect(keyAbove!);
    }

    return keyAbove;
  }

  getKeyPageBelow(key: Key) {
    let itemRect = this.getItemRect(key);

    if (!itemRect) {
      return null;
    }

    let keyPageBelow = key;
    const pageHeight = this.getPageHeight();
    const pageY = Math.min(this.getContentHeight()!, itemRect.y + pageHeight!);

    while (itemRect && itemRect.maxY < pageY) {
      const nextKey = this.getKeyBelow(keyPageBelow);
      itemRect = this.getItemRect(nextKey!);

      // Guard against case where maxY of the last key is barely less than pageY due to rounding
      // and thus it attempts to set key to null
      if (nextKey != null) {
        keyPageBelow = nextKey;
      }
    }

    return keyPageBelow;
  }

  getKeyForSearch(search: string, fromKey?: Key) {
    if (!this.collator) {
      return null;
    }

    const collection = this.collection;
    let key: Key | null | undefined = fromKey ?? this.getFirstKey();

    // If the starting key is a cell, search from its parent row.
    const startItem = collection.getItem(key!);
    if (startItem?.type === 'cell') {
      key = startItem.parentKey;
    }

    let hasWrapped = false;
    while (key != null) {
      const item = collection.getItem(key);

      // check row text value for match
      if (item?.textValue) {
        const substring = item.textValue.slice(0, search.length);
        if (this.collator.compare(substring, search) === 0) {
          if (this.isRow(item) && this.focusMode === 'cell') {
            return getFirstItem(getChildNodes(item, this.collection))?.key;
          }

          return item.key;
        }
      }

      key = this.findNextKey(key);

      // Wrap around when reaching the end of the collection
      if (key == null && !hasWrapped) {
        key = this.getFirstKey();
        hasWrapped = true;
      }
    }

    return null;
  }
}
